// The phase an Orpheus turn is in, published by the backend on the workspace stream (ORP-22001)
// and shown as one live line in the waiting answer (ORP-28228, v3 `useOrpheusTurnProgress.js`).
// Contract: orphex_django_rag/docs/contracts/sse_events_stream.md#orpheus_chat_progress
//
// Frames are tagged with the `client_turn_id` the chat POST carried: a new thread's first turn has
// no thread id yet, and every other tab of the workspace hears the same stream.

export const ORPHEUS_PROGRESS_EVENT = 'orpheus_chat_progress'

/** The phases the line names, in the order a data question passes through them. */
export const PROGRESS_PHASES = [
  'started',
  'understanding',
  'fetching',
  'preparing',
  'composing',
] as const

export type TurnProgressPhase = (typeof PROGRESS_PHASES)[number]

/**
 * What the line shows: a named phase, or `working` — a seconds counter for a turn that never got
 * past `started` (the account's own OpenAI key publishes only `started` and the terminal frames,
 * and a hidden tab hears nothing at all).
 */
export type TurnProgressLine = TurnProgressPhase | 'working'

export interface TurnProgressFrame {
  clientTurnId: string
  /** Raw wire phase; `completed`/`failed` and unknown phases arrive here too. */
  phase: string
}

/** Narrow one `orpheus_chat_progress` payload; anything without a turn id and phase is dropped. */
export function parseTurnProgressFrame(payload: unknown): TurnProgressFrame | null {
  if (!payload || typeof payload !== 'object') return null
  const record = payload as Record<string, unknown>
  const clientTurnId = record.client_turn_id
  const phase = record.phase
  if (typeof clientTurnId !== 'string' || !clientTurnId) return null
  if (typeof phase !== 'string' || !phase) return null
  return { clientTurnId, phase }
}

/** A phase the line names. `completed`/`failed` are not: the chat response closes the box. */
export function isProgressPhase(phase: string): phase is TurnProgressPhase {
  return (PROGRESS_PHASES as readonly string[]).includes(phase)
}

/** The i18n key of a line; `working` interpolates `{seconds}`. */
export function progressLabelKey(line: TurnProgressLine): string {
  return `orpheus.stream.progress.${line}`
}
